import { Component, Vue, Watch } from "vue-property-decorator";
import DataTable from "@/components/data-table/DataTable";
import { Order } from "@/models/Order";
import { Contact as ContactModel } from "@/models/Contact";
import { UserFile } from "@/models/UserFile";
import { AxiosError } from "axios";
import ErrorHandler from "@/support/ErrorHandler";
import { customFilter } from "../Users/Users";
import OrderStatusOptions, { StatusOptions } from "@/support/OrderStatus";
import StyledBox, { StyledBoxOptions } from "@/support/StyledBoxType";
import {
  formatDate as format,
  sortObjectsArrayByProperty,
  currency, formatDate
} from "@/support/String";
import { DateTime } from "luxon";
import RenderFlow from "@/support/RenderFlow";
import { Options } from "@/components/mi-dialog/MiDialog";
import UpdateOrderDialog from "@/views/Orders/UpdateOrderDialog";
import { Stylist } from "@/models/Stylist";
import debounce from "debounce";
import LoaderCard from '@/components/loader-card/LoaderCard';
import { RoleEnum } from "@/models/User";

@Component<Orders>({
  components: {
    UpdateOrderDialog,
    LoaderCard,
  },
  filters: {
    formatDate(date: string) {
      return !date ? "-" : format(date, "dd-LL-yyyy");
    },
    formatTime(time: string) {
      return !time ? "" : format(time, "HH:mm");
    }
  }
})
export default class Orders extends Vue {
  public $pageTitle = "Orders";


  // filter values are persisted to the store when leaving the component and loaded when entering.

  protected contact: ContactModel = new ContactModel();
  protected stylisten: Stylist[] = [];
  protected selectedStylisten: Stylist[] = []; // used in the filter
  protected contactImages: any[] = [];

  protected selectedStatus: string = ""; // used in the filter
  protected selectedStyledBox: string = ""; // user in the filter
  protected selectedOrder: Order | null = null;
  protected selectedOrders: Order[] = [];
  protected selectedType: string = "";

  protected stylistDebounce: Function = debounce(this.handleStylistSearch, 300);
  protected stylistSearch: string = "";

  protected finalReturnDatePicker: boolean = false;
  protected finalReturnDate: string = "";

  protected isShowingFilters = false;
  protected filtersHeight = 0;
  protected isLoadingFilters = true;


  protected RenderFlow = new RenderFlow([
    "isMounted",
    "isLoadingStylists"
  ]);
  /**
   * when true bulk update dialog is shown.
   * falsified by finishing update
   *
   * @protected
   * @type {Boolean}
   * @memberof Orders
   */
  protected isUpdatingOrderBulk: Boolean = false;

  protected filters: customFilter = {
    type: "custom",
    items: this.statusOptions
  };

  // #region lifecycle methods

  public created() {
    // get the filter values.
    const gotFilters = this.$store.getters.orderFilters
    if (!!gotFilters) {

      this.selectedStatus = gotFilters.selectedStatus;
      this.selectedStyledBox = gotFilters.selectedStyledBox;
      this.selectedStylisten = gotFilters.selectedStylisten;

    }
  }

  public async mounted(): Promise<void> {
    this.RenderFlow.set("isMounted", true);

    this.$root.$emit("breadcrumbUpdated", {
      crumb: [{ name: this.$pageTitle }]
    });

    await this.getStylists();
    this.isLoadingFilters = false;
  }

  public destroyed() {
    // persist the filter values
    this.$store.dispatch('updateOrderFilters', {
      selectedStatus: this.selectedStatus,
      selectedStyledBox: this.selectedStyledBox,
      selectedStylisten: this.selectedStylisten
    });
  }

  // #endregion lifecycle methods

  protected showFilters(): void {
    if (!this.isShowingFilters) {
      this.isShowingFilters = true;
      const innerFilters = document.getElementsByClassName('inner-filters');
      this.filtersHeight = innerFilters[0].clientHeight;
    } else {
      this.isShowingFilters = false;
      this.filtersHeight = 0;
    }
  }

  protected handleStylistSearch(query: string) {
    this.RenderFlow.set("isLoadingStylists", true);
    this.getStylists(query);
  }

  protected async getStylists(query?: string) {
    await new Stylist()
      .filter("search", query)
      .limit(100)
      .sort("name", "ASC")
      .all()
      .then((stylists: Stylist[]) => {
        this.stylisten = stylists;
        this.RenderFlow.set("isLoadingStylists", false);
      })
      .catch((error: AxiosError) => {
        this.RenderFlow.set("isLoadingStylists", false);
        ErrorHandler.network(error);
      });
  }

  protected dateTime(date: string) {
    if (!date || date === "0000-00-00 00:00:00") {
      return "N/A";
    }

    return DateTime.fromSQL(date).toFormat("dd-LL-yyyy");
  }

  get tableOptions() {
    return {
      name: {
        singular: "Order",
        plural: this.$tc('menu.orders')
      },
      sort: {
        key: "created_at",
        order: "DESC"
      },
      headers: [
        {
          text: this.$tc("tableHeaders.orderNumber.title"),
          value: "peoplevox_id",
          class: "linkable",
          sortable: {
            key: "status",
            order: "ASC"
          },
          transform: (value: string) => {
            return value ? value : "-";
          },
          action: "/webshop/order/{id}"
        },
        {
          text: this.$tc("tableHeaders.client.title"),
          class: this.$store.state.Auth.canViewUsers() ? 'linkable' : '',
          value: "user_display_name",
          transform: (value: string) => {
            return value ? value : "-";
          },
          action: this.$store.state.Auth.canViewUsers() ? "/contacten/{user.id}" : '',
          sortable: {
            key: "user_display_name",
            order: "ASC"
          }
        },
        {
          text: this.$tc("tableHeaders.type.title"),
          value: "box_type",
          sortable: {
            key: "box_type",
            order: "ASC"
          },
          transform: (value: string) => {
            const styledBox = StyledBox.styledBoxOptions.find(
              (styledBox: StyledBoxOptions) => {
                return value === styledBox.value;
              }
            );
            if (typeof styledBox !== "undefined") {
              return (styledBox as StyledBoxOptions).text;
            } else {
              return "";
            }
          }
        },
        {
          text: this.$tc("tableHeaders.quantity.title"),
          value: "quantity",
          class: "qt-short",
          sortable: {
            key: "quantity",
            order: "ASC"
          }
        },
        {
          text: this.$tc("tableHeaders.quantitySold.title"),
          value: "sold_items",
          class: "qt-short",
          sortable: {
            key: "sold_items",
            order: "ASC"
          }
        },
        {
          text: this.$tc("tableHeaders.netSales.title"),
          value: "total_without_vat",
          sortable: {
            key: "total_without_vat",
            order: "ASC"
          },
          transform: (value: number) => {
            return value ? currency(value) : "";
          }
        },
        {
          text: this.$tc("tableHeaders.status.title"),
          value: "status",
          transform: (value: string) => {
            const capitalizedStatus = value.charAt(0).toUpperCase() + value.slice(1);
            return `<span class="statusPill statusPill--${value}">${capitalizedStatus}</span>`;
          },
          sortable: {
            key: "status",
            order: "ASC"
          }
        },
        {
          text: this.$tc("tableHeaders.createdAt.title"),
          value: "created_at",
          class: "text-right",
          transform: (date: string) => {
            return this.dateTime(date);
          },
          sortable: {
            key: "created_at",
            order: "ASC"
          }
        },
        {
          text: this.$tc("tableHeaders.finalized.title"),
          value: "finalized_at",
          class: "text-right",
          sortable: {
            key: "finalized_at",
            order: "ASC"
          },
          transform: (date: string) => {
            return this.dateTime(date);
          }
        },
        {
          text: this.$tc("tableHeaders.dispatched.title"),
          value: "dispatched_at",
          class: "text-right",
          sortable: {
            key: "dispatched_at",
            order: "ASC"
          },
          transform: (date: string) => {
            return this.dateTime(date);
          }
        },
        {
          text: this.$tc("tableHeaders.finalReturn.title"),
          value: "final_return_date",
          class: "text-right",
          sortable: {
            key: "final_return_date",
            order: "ASC"
          },
          transform: (date: string) => {
            return this.dateTime(date);
          }
        },
        {
          text: this.$tc("tableHeaders.returned.title"),
          value: "returned_at",
          class: "text-right",
          sortable: {
            key: "returned_at",
            order: "ASC"
          },
          transform: (date: string) => {
            return this.dateTime(date);
          }
        },
        {
          text: this.$tc("tableHeaders.stylist.title"),
          value: "stylist_name",
          sortable: {
            key: "stylist_name",
            order: "ASC"
          }
        }
      ],
      actions: [
        {
          type: "view",
          label: "view",
          icon: "remove_red_eye",
          action: "/webshop/order/{id}",
          tooltip: this.$tc("orders.checkOrder"),
          visible: (datatable: DataTable, order: Order) => {
            return order.status !== "cancelled";
          }
        },
        {
          type: "invoice",
          label: "invoice",
          icon: "euro_symbol",
          tooltip: "Invoice order",
          action: (datatable: DataTable, order: Order) => {
            if (!order) {
              return;
            }
            this.selectedOrder = order;
            this.$store.dispatch("openDialog", this.dialogInvoiceOptions);
          },
          visible: (datatable: DataTable, order: Order) => {
            return this.$store.state.Auth.canViewInvoices() && (order.status === "checkout");
          }

        },
        {
          type: "delete",
          label: "delete",
          icon: "delete",
          tooltip: "Verwijder order",
          action: (datatable: DataTable, order: Order) => {
            this.selectedOrder = order;
            this.$store.dispatch("openDialog", this.deleteDialogOptions);
          },
          visible: (datatable: DataTable, order: Order) => {
            if (!this.$store.state.Auth.canEditOrders()) {
              return false;
            }

            if (order.status === 'inactive') {
              return true;
            }

            // An admin can always delete an active order
            // A stylist only if it belongs to him/her
            if (order.status === 'active') {
              if (!this.$store || !this.$store.state || !this.$store.state.Auth) {
                return false;
              }

              if (this.$store.state.Auth.hasRole([RoleEnum.ADMINISTRATOR, RoleEnum.ROOT])) {
                return true;
              }

              if (order.stylist_id === this.$store.state.Auth.id) {
                return true;
              }
            }

            return false;
          }
        }
      ],
      // add: () => {
      //   this.isCreating = true;
      // },
      bulkUpdateDropdown: [

        {
          text: "Orders invoicen",
          icon: "euro_symbol",
          action: () => {
            this.$store.dispatch("openDialog", this.dialogManyInvoiceOptions);
          }
        },
        {
          text: "Update status",
          icon: "update",
          action: () => {
            this.isUpdatingOrderBulk = true;
            this.selectedType = "status";
          }
        },
        {
          text: "Update type",
          icon: "inbox",
          action: () => {
            this.isUpdatingOrderBulk = true;
            this.selectedType = "box-types";
          }
        }
      ],
      model: new Order()
        .include(["user", "order-items"])
        .filter({
          status: this.selectedStatus,
          box_type: this.selectedStyledBox,
          stylists: this.selectedStylisten,
          final_return_date: this.finalReturnDate,
        })
    };
  }

  protected redirectToOrder(order: any) {
    this.$router.push({
      name: "webshop.order",
      params: { contact: this.contact as any, id: order.id }
    });
  }

  public userCreated() {
    this.refreshTable();
  }

  public refreshTable() {
    this.isUpdatingOrderBulk = false;

    if (this.orderDatatable) {
      this.deselectCheckboxes();
      this.orderDatatable.refresh();
    }
  }

  public deselectCheckboxes() {
    if (this.orderDatatable) {
      this.selectedOrders = [];
      this.orderDatatable.selected = [];
    }
  }

  public clearFilter() {
    this.finalReturnDate = '';
    this.finalReturnDatePicker = false;
    this.refreshTable();
  }

  protected close() {
    this.$emit("input", false);
  }

  protected createInvoice() {
    if (!this.selectedOrder) {
      return;
    }

    const payload = {
      orders: [this.selectedOrder.id]
    };

    new Order()
      .createInvoice(payload)
      .then(() => {
        this.refreshTable();
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  get deleteDialogOptions(): Options {
    return {
      title:
        (this.selectedOrder as Order).id ||
        this.$t("dialogOptions.confirmation").toString(),
      text: "Weet je zeker dat je deze order wilt verwijderen?",
      type: "warning",
      buttons: {
        confirm: {
          text: this.$t("dialogOptions.button.delete").toString(),
          action: () => {
            if (!this.selectedOrder) {
              return;
            }

            new Order()
              .deleteOrder(this.selectedOrder.id as string)
              .then((response: any) => {
                if (response) {
                  this.refreshTable();
                  this.selectedOrder = null;
                }
              })
              .catch((error: AxiosError) => {
                ErrorHandler.network(error);
              });
          }
        },
        cancel: {
          text: this.$t("dialogOptions.button.cancel").toString(),
          action: () => {
            this.selectedOrder = null;
          }
        }
      }
    };
  }

  get dialogInvoiceOptions(): Options {
    return {
      title: this.$t("dialogOptions.confirmation").toString(),
      text: "Weet je zeker dat je deze order wilt invoicen?",
      type: "warning",
      buttons: {
        confirm: {
          text: "Invoicen",
          action: () => {
            this.createInvoice();
          }
        },
        cancel: {
          text: this.$t("dialogOptions.button.cancel").toString(),
          action: () => {
            this.selectedOrder = null;
          }
        }
      }
    };
  }

  get dialogManyInvoiceOptions(): Options {
    const orders = this.selectedOrders.filter((order: Order) => {
      return order.status === "checkout";
    });

    return {
      title: "Orders invoicen",
      text: `${orders.length} van de ${this.selectedOrders.length} worden geinvoiced`,
      type: "warning",
      buttons: {
        confirm: {
          text: "Invoicen",
          action: () => {
            const ordersId = orders.map((order: Order) => {
              return order.id;
            });

            const payload = {
              orders: ordersId
            };

            new Order()
              .createInvoice(payload)
              .then(() => {
                this.refreshTable();
              })
              .catch((error: AxiosError) => {
                ErrorHandler.network(error);
              });
          }
        },
        cancel: {
          text: this.$t("dialogOptions.button.cancel").toString(),
          action: () => {
            this.selectedOrder = null;
          }
        }
      }
    };
  }

  protected get orderDatatable(): DataTable | null {
    const dataTable = this.$refs.orderDatatable;
    if (dataTable) {
      return dataTable as DataTable;
    }

    return null;
  }

  protected getFinalReturnDateStyle(date: string) {
    if (!date) {
      return 'color: gray;';
    }

    return '';
  }

  protected formattedEventDate(date: string) {
    if (!date) {
      return this.$tc('filters.finalReturnDate');
    }
    return formatDate(date);
  }

  protected get canBulkUpdate() {
    if (!this.$store || !this.$store.state || !this.$store.state.Auth) {
      return false;
    }

    return this.$store.state.Auth.hasRole([RoleEnum.ADMINISTRATOR, RoleEnum.ROOT]);
  }

  public get styledBoxOptions(): StyledBoxOptions[] {
    return StyledBox.styledBoxOptions;
  }

  public get statusOptions(): StatusOptions[] {
    return sortObjectsArrayByProperty(OrderStatusOptions.statusOptions, "value");
  }


  @Watch("stylistSearch")
  protected stylistSearchChanged(query: string) {
    if (this.RenderFlow.get("isLoadingStylists")) {
      return;
    }

    if (query && query.length) {
      this.stylistDebounce(query);
    }
  }
}
